'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useShopifyConnectionHealth, type ShopifyConnectionHealth } from '@/lib/api/integrations';

type HealthState = ShopifyConnectionHealth['state'];
type Fix = 'reconnect' | 'billing' | 'support' | null;

/** Red: Shopify gets no order at all. Amber: one part is held, the rest works. */
const PROBLEMS: Record<Exclude<HealthState, 'healthy'>, { tone: 'red' | 'amber'; fix: Fix }> = {
  reconnect_needed: { tone: 'red', fix: 'reconnect' },
  bill_unpaid: { tone: 'red', fix: 'billing' },
  shop_unavailable: { tone: 'red', fix: 'support' },
  store_deleted: { tone: 'red', fix: null },
  locked: { tone: 'red', fix: 'support' },
  permissions_missing: { tone: 'amber', fix: 'reconnect' },
  checkout_button_missing: { tone: 'amber', fix: null },
  checkout_button_other_store: { tone: 'amber', fix: null },
};

const SHOPIFY_SUPPORT_URL = 'https://help.shopify.com/en/support';

function billingUrlOf(shopUrl: string): string {
  const handle = /^(?:https?:\/\/)?([a-z0-9][a-z0-9-]*)\.myshopify\.com/i.exec(shopUrl.trim())?.[1];
  return handle === undefined
    ? 'https://admin.shopify.com/'
    : `https://admin.shopify.com/store/${handle.toLowerCase()}/settings/billing`;
}

interface ShopifyCredentialsBannerProps {
  storeId: string;
  shopUrl: string;
  canReconnect: boolean;
  onReconnect: () => void;
  connectedShopName: string;
  connectedViaOAuth: boolean;
}

interface FixProps {
  fix: Fix;
  shopUrl: string;
  canReconnect: boolean;
  onReconnect: () => void;
}

function HealthFix({ fix, shopUrl, canReconnect, onReconnect }: FixProps) {
  const { t } = useTranslation('integrations');
  if (fix === 'billing' || fix === 'support') {
    return (
      <Button asChild type="button" size="sm" variant="outline">
        <a href={fix === 'billing' ? billingUrlOf(shopUrl) : SHOPIFY_SUPPORT_URL} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="mr-2 h-4 w-4" />
          {t(fix === 'billing' ? 'shopify.health.fix.billing' : 'shopify.health.fix.support')}
        </a>
      </Button>
    );
  }
  if (fix !== 'reconnect') {
    return null;
  }
  return canReconnect ? (
    <Button type="button" size="sm" className="bg-[#96bf48] hover:bg-[#7a9e3a]" onClick={onReconnect}>
      <ExternalLink className="mr-2 h-4 w-4" />
      {t('shopify.credentialStatus.reconnectCta')}
    </Button>
  ) : (
    <p className="text-xs">{t('shopify.credentialStatus.reconnectManualHint')}</p>
  );
}

/**
 * What the daily probe last found on the store's Shopify connection, read from the health row: the
 * form no longer probes Shopify each time it opens.
 */
export function ShopifyCredentialsBanner({
  storeId,
  shopUrl,
  canReconnect,
  onReconnect,
  connectedShopName,
  connectedViaOAuth,
}: ShopifyCredentialsBannerProps) {
  const { t, i18n } = useTranslation('integrations');
  const query = useShopifyConnectionHealth(storeId);
  const health = query.data;

  if (query.isLoading || query.isError) {
    return null;
  }

  if (health && health.state !== 'healthy') {
    const problem = PROBLEMS[health.state];
    const features = health.missingFeatures.map((feature) => t(`shopify.health.feature.${feature}`)).join(', ');
    const since = health.since
      ? new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(new Date(health.since))
      : null;
    const red = problem.tone === 'red';
    return (
      <Alert
        variant="destructive"
        className={red ? 'border-red-500 bg-red-50 text-red-900' : 'border-amber-500 bg-amber-50 text-amber-900'}
        data-testid="shopify-health-banner"
      >
        {red ? <AlertCircle className="h-4 w-4 text-red-600" /> : <AlertTriangle className="h-4 w-4 !text-amber-600" />}
        <AlertTitle className="font-semibold">{t(`shopify.health.${health.state}.title`)}</AlertTitle>
        <AlertDescription className={red ? 'text-red-800' : 'text-amber-800'}>
          <p className="mb-3">
            {t(`shopify.health.${health.state}.description`, { shopName: connectedShopName, features })}
            {since && <span className="ml-1 text-xs">{t('shopify.health.since', { date: since })}</span>}
          </p>
          <HealthFix fix={problem.fix} shopUrl={shopUrl} canReconnect={canReconnect} onReconnect={onReconnect} />
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="border-green-200 bg-green-50">
      <CheckCircle className="h-4 w-4 text-green-600" />
      <AlertDescription className="text-green-800">
        {t('shopify.connectedTo', { shopName: connectedShopName })}
        {connectedViaOAuth && <span className="ml-2 text-xs text-green-600">{t('shopify.viaOAuth')}</span>}
      </AlertDescription>
    </Alert>
  );
}
